import { AdvancedShootTimeAccordion } from "./AdvancedShootTimeAccordion";
import { BasicShootTimeCard } from "./BasicShootTimeCard";
import { Formula } from "protos/almanac/almanac";
import {
  formulaFieldId,
  FormulaFieldKey,
  getFormulasFromBands,
  MULTIPLIER_FORMULA_FIELD_KEY,
  ShootTimeBand,
} from "portal/utils/shootTime";
import { WhenTrue } from "common/utils/types";
import React, { FC, useCallback } from "react";

type ShootTimeValuesProps = {
  bands: ShootTimeBand[];
  sharedAdvancedFormula?: Formula;
} & WhenTrue<
  "canEdit",
  {
    onChange: (formulas: Formula[]) => void;
    onFieldValidityChange: (fieldId: string, isValid: boolean) => void;
  }
>;

const bandKey = (band: ShootTimeBand): string =>
  `${band.bounds.lower}-${band.bounds.upper}`;

export const ShootTimeValues: FC<ShootTimeValuesProps> = ({
  bands,
  sharedAdvancedFormula,
  canEdit,
  onChange,
  onFieldValidityChange,
}) => {
  const onFormulaChange = useCallback(
    (
      field: FormulaFieldKey,
      value: number,
      bandIndex?: number // no bandIndex means apply to all!
    ): void => {
      if (!canEdit) {
        return;
      }
      const formulas = getFormulasFromBands(bands).map((formula, index) => {
        if (bandIndex !== undefined && index !== bandIndex) {
          return structuredClone(formula);
        }
        return { ...structuredClone(formula), [field]: value };
      });
      onChange(formulas);
    },
    [bands, canEdit, onChange]
  );

  const onMultiplierChange = useCallback(
    (bandIndex: number) => (value: number) => {
      onFormulaChange(MULTIPLIER_FORMULA_FIELD_KEY, value, bandIndex);
    },
    [onFormulaChange]
  );

  const onMultiplierValidityChange = useCallback(
    (bandIndex: number) => (isValid: boolean) => {
      if (!canEdit) {
        return;
      }
      onFieldValidityChange(
        formulaFieldId(`band-${bandIndex}`, MULTIPLIER_FORMULA_FIELD_KEY),
        isValid
      );
    },
    [canEdit, onFieldValidityChange]
  );

  const onAdvancedValidityChange = useCallback(
    (scope: string) => (field: FormulaFieldKey, isValid: boolean) => {
      if (!canEdit) {
        return;
      }
      onFieldValidityChange(formulaFieldId(scope, field), isValid);
    },
    [canEdit, onFieldValidityChange]
  );

  if (sharedAdvancedFormula) {
    // one advanced formula section below all the basic cards
    return (
      <div className="flex flex-col gap-2 min-w-[12rem] w-full">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          {bands.map((band, bandIndex) => (
            <div key={bandKey(band)} className="flex-1 min-w-[15rem]">
              {canEdit ? (
                <BasicShootTimeCard
                  band={band}
                  canEdit
                  onMultiplierChange={onMultiplierChange(bandIndex)}
                  onValidityChange={onMultiplierValidityChange(bandIndex)}
                />
              ) : (
                <BasicShootTimeCard band={band} />
              )}
            </div>
          ))}
        </div>
        {canEdit ? (
          <AdvancedShootTimeAccordion
            formula={sharedAdvancedFormula}
            canEdit
            onFieldChange={(field, value) => {
              onFormulaChange(field, value);
            }}
            onFieldValidityChange={onAdvancedValidityChange("shared")}
          />
        ) : (
          <AdvancedShootTimeAccordion formula={sharedAdvancedFormula} />
        )}
      </div>
    );
  }

  // a separate advanced formula section for every card
  return (
    <div className="flex flex-col gap-3 min-w-[12rem] w-full">
      {bands.map((band, bandIndex) => (
        <div key={bandKey(band)} className="w-full">
          {canEdit ? (
            <BasicShootTimeCard
              band={band}
              canEdit
              onMultiplierChange={onMultiplierChange(bandIndex)}
              onValidityChange={onMultiplierValidityChange(bandIndex)}
            >
              <AdvancedShootTimeAccordion
                formula={band.formula}
                outlined={false}
                canEdit
                onFieldChange={(field, value) => {
                  onFormulaChange(field, value, bandIndex);
                }}
                onFieldValidityChange={onAdvancedValidityChange(
                  `band-${bandIndex}`
                )}
              />
            </BasicShootTimeCard>
          ) : (
            <BasicShootTimeCard band={band}>
              <AdvancedShootTimeAccordion
                formula={band.formula}
                outlined={false}
              />
            </BasicShootTimeCard>
          )}
        </div>
      ))}
    </div>
  );
};
